/* "+ New" in the top bar: Check · Expense · Bank deposit · Transfer · Bill · Bill payment · Journal entry. TxFormHost renders the chosen form anywhere in the app. */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, PenLine, CreditCard, Landmark, ArrowLeftRight, FileText, Receipt, BookOpen, ChevronDown } from "lucide-react";
import { useLegacyApi } from "@/hooks/useLegacy";
import { MoneyOutForm } from "@/components/books/MoneyOutForm";
import { BillPaymentForm } from "@/components/books/BillPaymentForm";
import { DepositForm, TransferForm } from "@/components/books/BankForms";

export const openNewTx = (kind, prefill = {}) => window.dispatchEvent(new CustomEvent("tp:new-tx", { detail: { kind, prefill } }));
const changed = () => window.dispatchEvent(new CustomEvent("tp:tx-saved"));

const ITEMS = [
  { kind: "check", label: "Check", icon: PenLine, hint: "pay someone from a bank account, print it" },
  { kind: "expense", label: "Expense", icon: CreditCard, hint: "card, ACH or petty-cash purchase" },
  { kind: "deposit", label: "Bank deposit", icon: Landmark, hint: "money into a bank account" },
  { kind: "transfer", label: "Transfer", icon: ArrowLeftRight, hint: "between two of your accounts" },
  { kind: "bill", label: "Bill", icon: FileText, hint: "what a vendor says you owe" },
  { kind: "bill_payment", label: "Bill payment", icon: Receipt, hint: "pay bills you already entered" },
  { kind: "journal", label: "Journal entry", icon: BookOpen, hint: "debits and credits by hand" },
];

export const NewTxButton = () => {
  const [open, setOpen] = useState(false);
  const nav = useNavigate();
  const go = (it) => {
    setOpen(false);
    if (it.kind === "bill") nav("/business/bills?new=new");
    else if (it.kind === "journal") nav("/business/reports?tab=je&new=1");
    else openNewTx(it.kind);
  };
  return (
    <div className="relative shrink-0">
      <button type="button" data-testid="new-tx-button" onClick={() => setOpen((o) => !o)} className="inline-flex items-center gap-1.5 rounded-full bg-[#663399] px-3 sm:px-4 py-1.5 text-sm font-bold text-white shadow-md shadow-[#663399]/30 transition-transform hover:-translate-y-px hover:bg-[#552b80]"><Plus className="h-4 w-4" /><span className="hidden sm:inline">New</span><ChevronDown className={`h-3.5 w-3.5 transition-transform ${open ? "rotate-180" : ""}`} /></button>
      {open && <>
        <div className="fixed inset-0 z-[55]" onClick={() => setOpen(false)} />
        <div data-testid="new-tx-menu" className="tp-pop fixed inset-x-3 top-16 sm:absolute sm:inset-x-auto sm:top-auto sm:left-0 z-[60] sm:mt-2 sm:w-72 rounded-2xl border border-gray-100 bg-white p-1.5 shadow-[0_16px_40px_-12px_rgba(16,24,40,0.3)]">
          <div className="px-3 pt-1.5 pb-1 text-[10px] font-extrabold uppercase tracking-wider text-gray-400">New transaction</div>
          {ITEMS.map((it) => { const Icon = it.icon; return (
            <button key={it.kind} type="button" data-testid={`new-tx-${it.kind}`} onClick={() => go(it)} className="flex w-full items-center gap-3 rounded-xl px-3 py-2 text-left hover:bg-[#663399]/[0.06]">
              <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-gray-100 text-gray-600"><Icon className="h-4 w-4" /></span>
              <span className="min-w-0"><span className="block text-sm font-bold text-gray-800">{it.label}</span><span className="block truncate text-[11px] text-gray-400">{it.hint}</span></span>
            </button>); })}
        </div>
      </>}
    </div>
  );
};

/* Listens for tp:new-tx and shows the right form; fires tp:tx-saved after a save so lists can refresh */
export const TxFormHost = () => {
  const api = useLegacyApi();
  const [tx, setTx] = useState(null);
  useEffect(() => { const h = (e) => setTx(e.detail); window.addEventListener("tp:new-tx", h); return () => window.removeEventListener("tp:new-tx", h); }, []);
  if (!tx || !api.storeId) return null;
  const close = () => setTx(null);
  const common = { api, onClose: close, onSaved: changed, prefill: tx.prefill || {} };
  const payBill = (vendor, bill) => setTx({ kind: "bill_payment", prefill: { vendor_id: vendor.id, bill_id: bill?.id, pay_all: !bill } });
  if (tx.kind === "check" || tx.kind === "expense") return <MoneyOutForm key={tx.kind} kind={tx.kind} {...common} onPayBill={payBill} />;
  if (tx.kind === "deposit") return <DepositForm {...common} />;
  if (tx.kind === "transfer") return <TransferForm {...common} />;
  if (tx.kind === "bill_payment") return <BillPaymentForm {...common} />;
  return null;
};

export const useTxSaved = (fn) => { useEffect(() => { window.addEventListener("tp:tx-saved", fn); return () => window.removeEventListener("tp:tx-saved", fn); }, [fn]); };
